import { Box, Dialog, useMediaQuery, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useId, useState } from 'react';

import { ColumnGroupSection } from './ColumnGroupSection';
import { ColumnSelectionProps } from './ColumnSelection.props';
import { ColumnSelectionEmptyState } from './ColumnSelectionEmptyState';
import { ColumnSelectionFooter } from './ColumnSelectionFooter';
import { ColumnSelectionHeader } from './ColumnSelectionHeader';
import {
  applyToggle,
  GroupView,
  setFieldsAcrossGroups,
  setFieldsInGroup,
} from './columnSelectionHelpers';
import { ColumnSelectionSidebar } from './ColumnSelectionSidebar';
import { useColumnSelectionFilters } from './useColumnSelectionFilters';

export const ColumnSelection: React.FC<ColumnSelectionProps> = ({
  open,
  title,
  value,
  onChange,
  onClose,
  onReset,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = useId();
  const summaryId = useId();

  const filters = useColumnSelectionFilters(open, value);
  const { views, totals, isFiltered, query, retainRow } = filters;

  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  // Measure the paper as it mounts — while it still shows the full, unfiltered
  // column list — then hold that height for the rest of the visit. Sizing to
  // content instead shrank the dialog on every keystroke in the search box and
  // snapped it back when the query was cleared, which reads as flicker.
  //
  // A callback ref rather than an effect keyed on `open`: MUI portals the
  // dialog and only mounts its children on a later commit, so such an effect
  // runs while the paper is still null. `offsetHeight` rather than a bounding
  // rect, because the dialog grows in on a transform and a rect would capture
  // the mid-animation scale. The paper unmounts on close, so the null call
  // clears the lock and the next visit measures afresh.
  //
  // `fullScreen` belongs in the deps, not in a separate effect: there is
  // nothing to lock at that size — the paper already fills the viewport — and
  // re-running the ref is what re-measures when the viewport crosses back.
  const measurePaper = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || fullScreen) {
        setLockedHeight(null);
        return;
      }
      setLockedHeight((previous) => previous ?? (node.offsetHeight || null));
    },
    [fullScreen]
  );

  // One group means the sidebar would name the only thing on screen — the two
  // import panels pass exactly that, so drop it and let the list run full width.
  const showSidebar = value.length > 1;
  const visibleCount = views.reduce((sum, view) => sum + view.items.length, 0);
  const trimmedQuery = query.trim();

  const handleToggleField = useCallback(
    (groupIndex: number, field: string, checked: boolean) => {
      if (!checked) retainRow(groupIndex, field);
      onChange(applyToggle(value, groupIndex, field, checked));
    },
    [onChange, retainRow, value]
  );

  const handleSetVisibleInGroup = useCallback(
    (view: GroupView, checked: boolean) => {
      const fields = new Set(view.items.map((item) => item.field));
      onChange(setFieldsInGroup(value, view.index, fields, checked));
    },
    [onChange, value]
  );

  const handleSetAllVisible = useCallback(
    (checked: boolean) => onChange(setFieldsAcrossGroups(value, views, checked)),
    [onChange, value, views]
  );

  const status = trimmedQuery
    ? `${visibleCount} ${visibleCount === 1 ? 'column matches' : 'columns match'} "${trimmedQuery}"`
    : `${totals.selected} of ${totals.total} columns selected`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      aria-describedby={summaryId}
      slotProps={{
        paper: {
          ref: measurePaper,
          sx: {
            // Fill most of the viewport so the widest pickers show several
            // columns of checkboxes at once, but stay bounded so a two-group
            // picker doesn't become a mostly-empty sheet.
            width: { xs: '100%', sm: 'min(1240px, calc(100vw - 48px))' },
            maxWidth: 'none',
            // Content-sized until the measurement lands, pinned to it after.
            // `maxHeight` still outranks the pin, so a locked height yields to
            // a viewport that has since shrunk.
            height: lockedHeight ?? { xs: '100%', sm: 'auto' },
            minHeight: { xs: '100%', sm: 420 },
            maxHeight: { xs: '100%', sm: 'calc(100vh - 48px)' },
            bgcolor: theme.palette.v4.surface,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
      }}
    >
      <ColumnSelectionHeader
        title={title}
        titleId={titleId}
        summaryId={summaryId}
        query={query}
        onQueryChange={filters.setQuery}
        selectedOnly={filters.selectedOnly}
        onToggleSelectedOnly={filters.toggleSelectedOnly}
        totals={totals}
        visibleCount={visibleCount}
        isFiltered={isFiltered}
        onSelectVisible={handleSetAllVisible}
      />

      <Box sx={visuallyHidden} role="status" aria-live="polite" aria-atomic>
        {status}
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {showSidebar && (
          <ColumnSelectionSidebar
            summaries={filters.summaries}
            activeGroup={filters.effectiveGroup}
            totals={totals}
            isSearching={Boolean(trimmedQuery)}
            onSelect={filters.setActiveGroup}
          />
        )}

        {/* `scrollbarGutter: stable` keeps the checkbox grid on the same
            column tracks whether or not the pane is currently scrollable — a
            search that trims the list past the scroll threshold would
            otherwise reclaim the scrollbar's width and reflow every row. */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}>
          {views.length === 0 ? (
            <ColumnSelectionEmptyState
              query={trimmedQuery}
              hasColumns={value.length > 0}
              onClear={filters.clearFilters}
            />
          ) : (
            views.map((view) => (
              <ColumnGroupSection
                key={view.index}
                view={view}
                isFiltered={isFiltered}
                showHeading={showSidebar}
                onToggleField={handleToggleField}
                onSetVisible={handleSetVisibleInGroup}
              />
            ))
          )}
        </Box>
      </Box>

      <ColumnSelectionFooter totals={totals} onReset={onReset} onDone={onClose} />
    </Dialog>
  );
};
